import _ from 'lodash'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useCasinoNavTypes } from '@/hooks/use-casino-nav-types'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { selectSelectedCasinoTypeId, setSelectedCasinoType } from '@/store/slices/casinoSlice'
import type { CasinoTypeId } from '@/types/casino'
import { CasinoTypeButton } from './CasinoTypeButton'
import { Button } from '@/components/ui/button'

export function CasinoTypeNav() {
	const dispatch = useAppDispatch()
	const selectedTypeId = useAppSelector(selectSelectedCasinoTypeId)
	const types = useCasinoNavTypes()
	const scrollRef = useRef<HTMLDivElement>(null)
	const [canScrollPrev, setCanScrollPrev] = useState(false)
	const [canScrollNext, setCanScrollNext] = useState(false)

	const updateScrollState = useCallback(() => {
		const container = scrollRef.current

		if (!container) {
			return
		}

		setCanScrollPrev(container.scrollLeft > 0)
		setCanScrollNext(container.scrollLeft + container.clientWidth < container.scrollWidth - 1)
	}, [])

	useEffect(() => {
		const container = scrollRef.current

		if (!container) {
			return
		}

		updateScrollState()
		container.addEventListener('scroll', updateScrollState, { passive: true })
		window.addEventListener('resize', updateScrollState)

		return () => {
			container.removeEventListener('scroll', updateScrollState)
			window.removeEventListener('resize', updateScrollState)
		}
	}, [updateScrollState, types])

	const handleScroll = (direction: 'prev' | 'next') => {
		const container = scrollRef.current

		if (!container) {
			return
		}

		container.scrollBy({
			left: direction === 'next' ? 240 : -240,
			behavior: 'smooth',
		})
	}

	const handleTypeClick = (typeId: CasinoTypeId) => {
		dispatch(setSelectedCasinoType(typeId))
	}

	return (
		<div className='relative flex items-center gap-2'>
			{canScrollPrev ? (
				<Button variant='secondary' size='icon' className='h-12 w-12' onClick={() => handleScroll('prev')} aria-label='Scroll types previous'>
					<ChevronLeft />
				</Button>
			) : null}

			<div ref={scrollRef} className='flex min-w-0 flex-1 gap-2 overflow-x-auto scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'>
				{_.map(types, (type) => (
					<CasinoTypeButton key={type.id} label={type.label} icon={type.icon} isActive={selectedTypeId === type.id} onClick={() => handleTypeClick(type.id)} />
				))}
			</div>

			{canScrollNext ? (
				<Button variant='secondary' size='icon' className='h-12 w-12' onClick={() => handleScroll('next')} aria-label='Scroll types next'>
					<ChevronRight />
				</Button>
			) : null}
		</div>
	)
}
